/**
 * Knowledge Base Admin Page
 * ==========================
 * Upload and manage knowledge documents for agent RAG
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GlobalHeader from '../../components/GlobalHeader';
import { 
  Upload, 
  BookOpen, 
  FileText, 
  Brain, 
  Check, 
  X, 
  Loader2,
  Search,
  AlertCircle,
  Sparkles,
  Trash2,
  Edit,
  Save
} from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

interface KnowledgeDocument {
  id: string;
  doc_type: 'kpi_definition' | 'retailer_playbook' | 'historical_insight';
  title: string;
  content: string;
  metadata: any;
  company_id: string | null;
  created_at: string;
  created_by: string;
}

const DOC_TYPES = [
  { value: 'kpi_definition', label: 'KPI Definition', icon: FileText, color: 'blue' },
  { value: 'retailer_playbook', label: 'Retailer Playbook', icon: BookOpen, color: 'green' },
  { value: 'historical_insight', label: 'Historical Insight', icon: Brain, color: 'purple' }
];

export default function KnowledgeBase() {
  const { companyId } = useTenant();
  const queryClient = useQueryClient();
  
  // Get user email from localStorage (set during login)
  const userEmail = localStorage.getItem('user_email') || 'system';
  
  // Form state
  const [docType, setDocType] = useState<string>('kpi_definition');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [metadata, setMetadata] = useState('{}');
  
  // UI state
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDoc, setEditingDoc] = useState<KnowledgeDocument | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  
  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch('/api/knowledge/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Upload failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      // Reset form
      setTitle('');
      setContent('');
      setMetadata('{}');
    }
  });
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/knowledge/delete?id=${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Delete failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      setDeleteConfirm(null);
    }
  });
  
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch('/api/knowledge/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Update failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-docs'] });
      setEditingDoc(null);
    }
  });
  
  // Fetch documents query (backend will filter by user's company)
  const { data: documents, isLoading } = useQuery<KnowledgeDocument[]>({
    queryKey: ['knowledge-docs'],
    queryFn: async () => {
      const response = await fetch(`/api/knowledge/list`);
      if (!response.ok) throw new Error('Failed to fetch documents');
      const json = await response.json();
      return json.data || [];
    },
    refetchInterval: 10000 // Refresh every 10s
  });
  
  const handleUpload = () => {
    if (!title || !content) {
      alert('Please fill in title and content');
      return;
    }
    
    let parsedMetadata = {};
    try {
      parsedMetadata = metadata ? JSON.parse(metadata) : {};
    } catch (e) {
      alert('Invalid JSON in metadata');
      return;
    }
    
    uploadMutation.mutate({
      docType,
      title,
      content,
      metadata: parsedMetadata,
      isGlobal, // Backend will determine company_id from auth
      createdBy: userEmail
    });
  };
  
  // Filter documents
  const filteredDocs = documents?.filter(doc => {
    const matchesType = filter === 'all' || doc.doc_type === filter;
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  }) || [];
  
  // Stats
  const totalDocs = documents?.length || 0;
  const kpiCount = documents?.filter(d => d.doc_type === 'kpi_definition').length || 0;
  const playbookCount = documents?.filter(d => d.doc_type === 'retailer_playbook').length || 0;
  const insightCount = documents?.filter(d => d.doc_type === 'historical_insight').length || 0;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <GlobalHeader />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Knowledge Base</h1>
              <p className="text-gray-600">Manage documents for agent RAG and semantic search</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Documents</p>
                <p className="text-3xl font-bold text-gray-900">{totalDocs}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">KPI Definitions</p>
                <p className="text-3xl font-bold text-blue-600">{kpiCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Playbooks</p>
                <p className="text-3xl font-bold text-green-600">{playbookCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Insights</p>
                <p className="text-3xl font-bold text-purple-600">{insightCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-8">
          {/* Upload Form */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Upload Document
            </h2>
            
            <div className="space-y-4">
              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {DOC_TYPES.map(type => {
                    const Icon = type.icon;
                    const isSelected = docType === type.value;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setDocType(type.value)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? `border-${type.color}-500 bg-${type.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${isSelected ? `text-${type.color}-600` : 'text-gray-400'}`} />
                        <span className={`font-medium ${isSelected ? `text-${type.color}-900` : 'text-gray-700'}`}>
                          {type.label}
                        </span>
                        {isSelected && <Check className={`w-5 h-5 ml-auto text-${type.color}-600`} />}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Weeks of Supply (WOS)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter the document content... This will be embedded and searchable by agents."
                  rows={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {content.length} characters
                </p>
              </div>
              
              {/* Metadata (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Metadata (JSON, optional)
                </label>
                <textarea
                  value={metadata}
                  onChange={(e) => setMetadata(e.target.value)}
                  placeholder='{"retailer": "ulta", "category": "inventory"}'
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-xs"
                />
              </div>
              
              {/* Global/Company */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isGlobal"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="isGlobal" className="text-sm text-gray-700">
                  Global document (available to all companies)
                </label>
              </div>
              
              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={uploadMutation.isPending || !title || !content}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading & Embedding...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload Document
                  </>
                )}
              </button>
              
              {/* Status Messages */}
              {uploadMutation.isError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">
                    {uploadMutation.error?.message || 'Upload failed'}
                  </p>
                </div>
              )}
              
              {uploadMutation.isSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800">
                    Document uploaded successfully! Embeddings generated.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Documents List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Existing Documents
            </h2>
            
            {/* Filters */}
            <div className="mb-4 space-y-3">
              {/* Type Filter */}
              <div className="flex gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    filter === 'all'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All ({totalDocs})
                </button>
                {DOC_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setFilter(type.value)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      filter === type.value
                        ? `bg-${type.color}-100 text-${type.color}-700`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {type.label.split(' ')[0]}s
                  </button>
                ))}
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search documents..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            {/* List */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : filteredDocs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No documents found</p>
                  <p className="text-sm text-gray-500 mt-1">Upload your first document to get started</p>
                </div>
              ) : (
                filteredDocs.map(doc => {
                  const docTypeInfo = DOC_TYPES.find(t => t.value === doc.doc_type);
                  const Icon = docTypeInfo?.icon || FileText;
                  
                  return (
                    <div
                      key={doc.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-${docTypeInfo?.color}-100 flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 text-${docTypeInfo?.color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1 truncate">
                            {doc.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {doc.content.substring(0, 150)}...
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className={`px-2 py-1 rounded bg-${docTypeInfo?.color}-100 text-${docTypeInfo?.color}-700`}>
                              {docTypeInfo?.label}
                            </span>
                            <span>{doc.company_id ? 'Company' : 'Global'}</span>
                            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingDoc(doc)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit document"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        
        {/* Edit Modal */}
        {editingDoc && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Document</h2>
                <button
                  onClick={() => setEditingDoc(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editingDoc.title}
                    onChange={(e) => setEditingDoc({ ...editingDoc, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <textarea
                    value={editingDoc.content}
                    onChange={(e) => setEditingDoc({ ...editingDoc, content: e.target.value })}
                    rows={10}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">{editingDoc.content.length} characters</p>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      updateMutation.mutate({
                        id: editingDoc.id,
                        title: editingDoc.title,
                        content: editingDoc.content
                      });
                    }}
                    disabled={updateMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving & Regenerating Embeddings...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setEditingDoc(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                
                {updateMutation.isError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{updateMutation.error?.message || 'Update failed'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Delete Document?</h2>
                  <p className="text-sm text-gray-600">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                The document and all its embeddings will be permanently deleted. Agents will no longer be able to find this information.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => deleteMutation.mutate(deleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleteMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      Delete Document
                    </>
                  )}
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
              
              {deleteMutation.isError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{deleteMutation.error?.message || 'Delete failed'}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

